import classes from "../../../../../../components/Popover/popover.module.scss";
import classesCB from "../../../styles/popover.module.scss";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import { Controller, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useAppDispatch } from "hooks/hooks";
import {
  produceNewInput,
  selectState,
  setNewOutputPopover,
  setOutputValues,
  setOwnProductPopover,
  setProductionDate
} from "../inputSlice";
import ConcreteBaseItemSelectCreate from "./ConcreteBaseItemSelectCreate";
import { concreteBaseService } from "Dashboard/scenes/ConcreteBase/services/concreteBase.service";
import { IProduceItem } from "Dashboard/scenes/ConcreteBase/domain/IProduceItem";
import { useSelector } from "react-redux";
import { IInputItemToTake } from "Dashboard/scenes/ConcreteBase/domain/IInputItemToTake";
import { useEffect, useRef, useState } from "react";
import { RiDownload2Fill } from "react-icons/ri";
import UnitSelect from "Dashboard/components/UnitSelect";
import { IConcreteBaseProducedItemResponse } from "Dashboard/scenes/ConcreteBase/domain/IConcreteBaseProduceItemResponse";
import NumberInput from "Dashboard/components/NumberInput";
import { FormatNumberWithUnitOrNull, numberToStringForInput } from "utils/numberUtils";
import { IInputsForProduction, IOneInputForProduction } from "Dashboard/scenes/ConcreteBase/domain/IInputsForProduction";
import { IRepository } from "Dashboard/scenes/ConcreteBase/domain/IRepository";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo } from "utils/authUtil";
import { CONCRETE_BASE_MODULE_NAME } from "Dashboard/scenes/ConcreteBase/domain/ConcreteBaseModule";

interface TableItem {
  itemName: string;
  quantity: number;
  unitName: string;
}

const OwnProductionPopover = () => {
  const {
    register,
    control,
    formState: { errors },
    getValues,
    setValue,
    handleSubmit,
    reset,
    trigger
  } = useForm<IProduceItem>();

  const dispatch = useAppDispatch();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [importedItems, setImportedItems] = useState<IInputsForProduction | undefined>(undefined);
  const [tableItems, setTableItems] = useState<TableItem[]>([]);

  const {
    inputs,
    outputValues: { map, repository }
  } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const canEditDate = hasFullAccessTo(user, CONCRETE_BASE_MODULE_NAME);

  const triggerFileDialog = () => {
    inputFileRef.current!.click();
  };

  useEffect(() => {
    if (importedItems !== undefined) {
      dispatch(setOutputValues(importedItems));
    }
  }, [dispatch, importedItems]);

  useEffect(() => {
    if (Object.keys(map).length > 0) {
      const items: TableItem[] = inputs
        .map(input => {
          const quantity = map[input.inputId];
          return quantity !== undefined
            ? {
              itemName: input.item.name,
              quantity: quantity,
              unitName: input.quantityUnit.name
            }
            : null;
        })
        .filter((item): item is TableItem => item !== null);

      setTableItems(items);
    }
  }, [dispatch, map, inputs]);

  const onFilechange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) {
      toast.error("Please select a file.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const fileItems = content
          .split('\n')
          .filter(Boolean)
          .map(line => {
            const [code, quantity] = line.split('\t');
            return {
              itemCode: code.trim(),
              quantity: Number(quantity.trim())
            };
          });

        let itemsToTake: IOneInputForProduction[] = [];
        let repositoryToTakeFrom: IRepository | undefined = undefined;
        for (const fileItem of fileItems) {
          let remainingQuantity = fileItem.quantity;
          for (let inputIndex = inputs.length - 1; inputIndex >= 0; inputIndex--) {
            const input = inputs[inputIndex];
            if (fileItem.itemCode === input.itemCode && input.quantityAmountRemaining > 0) {
              if (repositoryToTakeFrom !== undefined && repositoryToTakeFrom.repositoryId !== input.repository.repositoryId) {
                toast.error("Stavke u fajlu nisu iz istog magacina");
                return;
              }
              if (input.quantityAmountRemaining >= remainingQuantity) {
                if (repositoryToTakeFrom === undefined) {
                  repositoryToTakeFrom = input.repository;
                }
                itemsToTake.push({ inputId: input.inputId, quantity: remainingQuantity });
                remainingQuantity = 0;
                break;
              } else {
                if (repositoryToTakeFrom === undefined) {
                  repositoryToTakeFrom = input.repository;
                }
                itemsToTake.push({ inputId: input.inputId, quantity: input.quantityAmountRemaining });
                remainingQuantity -= input.quantityAmountRemaining;
              }
            }
          }
          if (remainingQuantity > 0) {
            toast.warn(`Ne može se proizvesti željeni artikal sa sastojcima iz fajla. Preostale količine sastojaka sa šifrom artikla "${fileItem.itemCode}" nisu dovoljne.`,
              { autoClose: 10000 }
            );
            return;
          }
        }

        if (itemsToTake.length > 0 && repositoryToTakeFrom !== undefined) {
          setImportedItems({ inputs: itemsToTake, repository: repositoryToTakeFrom });
        }
        toast.success("Fajl uspešno učitan.");
      } catch (error) {
        toast.error("Error parsing file content");
      }
    };

    reader.onerror = () => toast.error("Error reading file");
    reader.readAsText(selectedFile);
    e.target.value = '';
  };

  const onSubmit = async (data: any) => {
    const outputItems: IInputItemToTake[] = [];
    for (const [key, value] of Object.entries(map)) {
      outputItems.push({
        inputId: Number(key),
        quantity: value || 0,
      });
    }

    try {
      let response: IConcreteBaseProducedItemResponse;
      response = await concreteBaseService.produceOwnItem(
        {
          ...data,
          itemsToTake: outputItems,
          sourceRepositoryId: repository?.repositoryId
        });
      dispatch(produceNewInput(response));
      if (!response.producedInputs[0].item.canStayInRepository) {
        dispatch(setProductionDate(data.date));
        dispatch(setNewOutputPopover({ isOpen: true }));
      }
      close();
      toast.success("Artikal uspešno proizveden!");
    } catch (err: any) {
      if (err.response.status === 400) {
        toast.error("Molimo vas unesite odgovarajucu šifru artikla!");
      } else if (err.response.status === 412) {
        if (err.response.data.message === "Cannot create project because default cash meal rates are not set") {
          toast.error("Nije moguće automatski napraviti interni projekat za betonsku bazu. Proverite podešavanja podrazumevanih cena obroka u podešavanjima na početnoj strani.");
        }
      }
    }
  };

  const close = () => {
    dispatch(setOwnProductPopover({ isOpen: false }));
    reset();
  };

  return (
    <form className={`${classesCB.ownProdPopover} ${classes.basicPopover}`}
      onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data"
    >
      {Object.keys(map).length === 0 && <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir} ${classes.spaceBetween}`}>
          <label className={classes.text11}>
            Unesite parametre proizvedenog artikla:
          </label>
          <input type="file" ref={inputFileRef}
            style={{ display: "none" }} onChange={(e) => onFilechange(e)} />
          <div className={classes.buttons}>
            <button type="button"
              className={classes.iconButton}
              onClick={() => triggerFileDialog()}>
              <RiDownload2Fill className={classes.icon} />
            </button>
          </div>
        </div>
      </div>
      }

      {tableItems.length > 0 &&
        <div className={classesCB.table}>
          <div className={`g-grid g-header ${classesCB.gHeader}`}>
            <div className={`row ${classesCB.row}`}>
              <div className="col center lowercase separator-line">
                naziv stavke</div>
              <div className="col center lowercase separator-line">
                količina</div>
            </div>
          </div>
          <div
            className={`g-grid g-content ${classesCB.gContent}`}
          >
            {tableItems.map((item, index) => (
              <div className={`row ${classesCB.row}`} key={index}>
                <div className="col capitalize light">
                  <div className="bg-item">
                    {item.itemName}
                  </div>
                </div>
                <div className="col light">
                  <div className={"bg-item center"}>
                    {FormatNumberWithUnitOrNull(item.quantity, item.unitName)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      }

      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir} ${classesCB.cbItemWidth}
          ${errors.producedItemId ? classes.itemError : ""}`}>
          <label className={classes.aboveLabel}>naziv artikla</label>
          <Controller
            name="producedItemId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <ConcreteBaseItemSelectCreate
                setValue={setValue}
                trigger={trigger}
                code={"producedItemCode"}
                unit={"unit"}
                pricePerUnit={"price"}
                isOwnProduct={true}
                error={errors.producedItemId}
                customStyles={styles.customStyleClassic}
                onChange={onChange}
              />
            )}
          />
        </div>

        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>šifra artikla</label>
          <input type="text" className={classes.itemBorder} disabled
            {...register("producedItemCode", { required: false })} />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>cena</label>
          <Controller
            name="price"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <NumberInput
                formatAs="rsd"
                value={numberToStringForInput(getValues("price"))}
                className={`${classes.itemBorder} 
                  ${errors.price ? classes.itemError : ""}`}
                disabled
              />
            )}
          />
        </div>
      </div>

      {canEditDate &&
        <div className={`${classes.row} ${classes.rowDir}`}>
          <div className={`${classes.col} ${classes.colDir}`}>
            <label className={classes.aboveLabel}>datum</label>
            <Controller
              control={control}
              name="date"
              defaultValue={FormatDateIso(new Date())}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <div>
                  <DatePicker
                    onChange={(date) =>
                      onChange(date ? FormatDateIso(date) : undefined)
                    }
                    showMonthDropdown
                    showYearDropdown
                    dateFormat="dd.MM.yyyy"
                    selected={value ? new Date(value) : null}
                    className={`${classes.itemBorder} ${classes.dateItem} ${classesCB.cbItemWidth}
                      ${errors.date ? classes.itemError : ""}`}
                  />
                </div>
              )}
            />
          </div>
        </div>
      }

      <div className={`${classes.row} ${classes.rowDir} ${classesCB.rowQuantity}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>količina</label>
          <Controller
            name="quantity"
            control={control}
            defaultValue={undefined}
            rules={{ required: true, }}
            render={({ field: { onChange } }) => (
              <NumberInput
                formatAs="quantity"
                onChange={(
                  value: string,
                  unmaskedValue: string,
                  parsedValue: number) => onChange(
                    { target: { value: unmaskedValue } })}
                className={`${classes.itemBorder} 
                  ${errors.quantity ? classes.itemError : ""}`}
              />
            )}
          />
        </div>

        <div className={`${classes.col} ${classes.colDir} ${classesCB.qntU}
          ${errors.unit ? classes.itemError : ""}`}>
          <label className={classes.aboveLabel}>mera</label>
          <Controller
            name="unit"
            control={control}
            defaultValue={undefined}
            rules={{
              required: false,
            }}
            render={({ field: { onChange } }) => (
              <UnitSelect
                value={getValues("unit")}
                customStyles={styles.customStyleClassic}
                error={errors.unit}
                isDisabled={true}
                onChange={onChange}
              ></UnitSelect>
            )}
          />
        </div>

        <div className={`${classes.buttons} ${classes.bottom}`}>
          <button className={classes.iconButton} type="submit">
            <FontAwesomeIcon
              icon={faLongArrowAltDown}
              className={classes.icon} />
          </button>
        </div>
      </div>
    </form>
  )
};

export default OwnProductionPopover;
